import { useEffect, useState, ReactNode } from 'react'
import styled from 'styled-components'

import { mediaMax } from 'styles/media'
import typeStyle from 'styles/typeStyle'

import FilterButton from '../desktop/FilterButton'
import { Label, CheckboxToggle } from '../../../components/form'

type BookingOptionValues = {
  instantBook?: boolean
  fullRefund?: boolean
  oneNightStayWeekday?: boolean
  oneNightStayWeekend?: boolean
}

export const BOOKING_OPTIONS_FILTERS: (keyof BookingOptionValues)[] = [
  'instantBook',
  'fullRefund',
  'oneNightStayWeekday',
  'oneNightStayWeekend',
]

const BookingOptionsPopupContents = styled.div`
  max-width: 360px;
`

const BookingOptionToggleLabel = styled(Label)`
  cursor: pointer;
  overflow: auto;
  margin-bottom: 10px;

  & + & {
    margin-top: 26px;
  }

  > span {
    float: left;
    padding-right: 3.5em;

    > .description {
      ${typeStyle('-ds-font-body-light')}
      display: block;
      color: #999;
    }
  }

  > .toggle-checkbox {
    margin-left: auto;
  }

  ${mediaMax.md`
    overflow: hidden;
  `}
`

type BookingOptionToggleProps = {
  id: string
  name?: string
  label: string
  description: ReactNode
  isChecked?: boolean
  onChange: (isChecked: boolean) => void
}
const BookingOptionToggle = ({
  id,
  name,
  label,
  description,
  isChecked,
  onChange,
}: BookingOptionToggleProps) => (
  <BookingOptionToggleLabel>
    <span>
      {label}
      <span className="description">{description}</span>
    </span>
    <CheckboxToggle
      id={id}
      name={name || id}
      aria-label={label}
      isChecked={isChecked === true}
      onChange={(e) => onChange(e.target.checked)}
      style={{ marginTop: '0.1em' }}
    />
  </BookingOptionToggleLabel>
)

type BookingOptionsProps = {
  showOneNightStayOptions: boolean
  values: BookingOptionValues
  onChange: (nextFilters: BookingOptionValues) => void
}

export const BookingOptions = ({
  showOneNightStayOptions,
  values,
  onChange,
}: BookingOptionsProps) => {
  return (
    <>
      <BookingOptionToggle
        id="instant-book"
        label="Instant book"
        description="Listings you can book without waiting for your host to approve"
        isChecked={values.instantBook}
        onChange={(isChecked) => onChange({ instantBook: isChecked })}
      />
      <BookingOptionToggle
        id="full-refund"
        label="Full refund"
        description="Receive a full refund (minus service fee) up until 1 week prior to check-in"
        isChecked={values.fullRefund}
        onChange={(isChecked) => onChange({ fullRefund: isChecked })}
      />
      {showOneNightStayOptions && (
        <>
          <BookingOptionToggle
            id="one-night-stay-weekday"
            label="One night stay (weekday)"
            description="Listings that you can book for one night between Monday and Thursday"
            isChecked={values.oneNightStayWeekday}
            onChange={(isChecked) =>
              onChange({ oneNightStayWeekday: isChecked })
            }
          />
          <BookingOptionToggle
            id="one-night-stay-weekend"
            label="One night stay (weekend)"
            description="Listings that you can book for one night between Friday and Sunday"
            isChecked={values.oneNightStayWeekend}
            onChange={(isChecked) =>
              onChange({ oneNightStayWeekend: isChecked })
            }
          />
        </>
      )}
    </>
  )
}

type BookingOptionsFilterProps = BookingOptionsProps

const BookingOptionsFilter = ({
  showOneNightStayOptions,
  values,
  onChange,
}: BookingOptionsFilterProps) => {
  const [state, setState] = useState(values)
  const activeFilters = Object.keys(values).length

  // Override state if values passed in via props changes:
  useEffect(() => setState(values), [values])

  const handleClear = () => {
    // Set all values to null so that the `onChange` handler knows to clear out
    // those values from the query string:
    const resetState: BookingOptionValues = {}
    BOOKING_OPTIONS_FILTERS.forEach((key) => {
      resetState[key] = null
    })

    setState(resetState)
    onChange(resetState)
  }

  const handleChange = (changes: BookingOptionValues) => {
    setState({ ...state, ...changes })
  }

  return (
    <FilterButton
      label="Booking options"
      badge={activeFilters > 0 ? activeFilters.toString() : null}
      isActive={activeFilters > 0}
      onApply={() => onChange(state)}
      onClear={handleClear}
      onReset={() => setState(values)}
    >
      <BookingOptionsPopupContents>
        <BookingOptions
          showOneNightStayOptions={showOneNightStayOptions}
          values={state}
          onChange={handleChange}
        />
      </BookingOptionsPopupContents>
    </FilterButton>
  )
}

export default BookingOptionsFilter
